import linkifyIt from 'linkify-it'
import tlds from 'tlds'

const LINK_REGEXP = /mailto:|http:\/\/|https:\/\//

export const LINK_VALIDATOR = 'LINK_VALIDATOR'
/**
 * Validates if a link is a mailto or HTTP/HTTPS URL.
 *
 * @param link - The link string to validate
 * @returns True if the link matches mailto or HTTP/HTTPS, false otherwise
 */
export const validateLink = (link: string): boolean => LINK_REGEXP.test(link)

// don't include er domain as it causes issue for one of the customers:
// https://app.asana.com/0/35126173840535/1107707316797291/f
// TODO: needs longterm fix
const discoTlds = tlds.filter(t => t !== 'er')

/**
 * Instance of linkify-it configured with a filtered TLD list.
 * Used for matching and parsing links in text.
 */
export const linkify = new linkifyIt().tlds(discoTlds)

/**
 * Attempts to fix a link by ensuring it uses HTTPS if possible.
 *
 * If the link is matched and its URL differs, replaces with HTTPS.
 *
 * @param link - The link string to fix
 * @returns The fixed link string, with HTTPS if needed
 */
export const fixLink = (link: string): string => {
  const links = linkify.match(link.trim())
  if (
    links &&
    links.length === 1 &&
    links[0].raw === link &&
    links[0].url !== link
  ) {
    // Default to https
    // TODO: Replace 'linkify-it' with alternative
    return links[0].url.replace(/http:\/\/(?:https?)?/, 'https://')
  }
  return link
}

/**
 * Converts a string to a URL parts object (host, href, protocol).
 *
 * Takes a string that may or may not be a properly formatted URL and
 * parses it into component parts, adding protocol if necessary.
 *
 * @param string - The string to convert to URL format
 * @returns Object containing host, href, and protocol components
 *
 * @example
 * ```typescript
 * convertStringToURL('example.com')
 * // { host: 'example.com', href: 'https://example.com', protocol: 'https:' }
 *
 * convertStringToURL('https://example.com')
 * // { host: 'example.com', href: 'https://example.com', protocol: 'https:' }
 * ```
 */
export const convertStringToURL = (
  string: string,
): {
  host: string
  href: string
  protocol: string
} => {
  const matches = string.match(/^(?:(https?:)\/\/)?(.*)/i)

  let protocol = matches?.[1] ?? ''
  const host = matches?.[2] ?? ''

  if (!string.startsWith('http'.slice(0, string.length))) {
    protocol = 'https:'
  }

  return {
    host,
    href: protocol ? `${protocol}//${host}` : host,
    protocol,
  }
}

/**
 * Music platform provider types.
 * Matches LinkInfoProvider from @disco/types/search-page for consistency.
 */
export type MusicProvider = 'spotify' | 'youtube' | 'applemusic' | 'disco'

/**
 * Determines the music platform provider from a URL.
 *
 * @param url - The URL to check for music platform providers
 * @returns The provider type if detected, null if no supported provider found or empty URL
 *
 * @example
 * ```typescript
 * getMusicProviderFromUrl('https://open.spotify.com/track/123')
 * // 'spotify'
 *
 * getMusicProviderFromUrl('https://www.youtube.com/watch?v=123')
 * // 'youtube'
 *
 * getMusicProviderFromUrl('https://music.apple.com/track/123')
 * // 'applemusic'
 *
 * getMusicProviderFromUrl('https://example.com')
 * // null
 * ```
 */
export const getMusicProviderFromUrl = (url: string): MusicProvider | null => {
  if (!url) return null
  const lowerUrl = url.toLowerCase()
  if (lowerUrl.includes('spotify.com')) return 'spotify'
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be'))
    return 'youtube'
  if (
    lowerUrl.includes('music.apple.com') ||
    lowerUrl.includes('itunes.apple.com')
  )
    return 'applemusic'

  if (
    lowerUrl.includes('s.disco') ||
    lowerUrl.includes('disco.ac') ||
    lowerUrl.includes('disco-staging-2')
  )
    return 'disco'

  return null
}

/**
 * Tolerant of accidental whitespace inside a pasted URL (e.g. "youtube.co m")
 */
export const getMusicProviderFromSearchQuery = (
  query: string,
): MusicProvider | null => getMusicProviderFromUrl(query.replace(/\s+/g, ''))

/**
 * Sanitizes a URL to only allow safe protocols (http, https, mailto).
 *
 * Rejects dangerous protocols (javascript:, data:, vbscript:), embedded
 * credentials (https://user:pass@host), and mailto: targets containing
 * HTML-injection characters or missing an `@`.
 */
export function sanitizeUrl(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return ''
  if (!/^(https?:\/\/|mailto:)/i.test(trimmed)) return ''
  // mailto: URLs have different semantics and may not parse with the URL constructor.
  // Validate basic structure and reject characters that could enable HTML injection.
  if (/^mailto:/i.test(trimmed)) {
    if (/^mailto:[^\s<>"]+@[^\s<>"]+$/i.test(trimmed)) return trimmed
    return ''
  }
  try {
    const parsed = new URL(trimmed)
    // Reject URLs with embedded credentials (e.g., https://user:pass@evil.com)
    // to prevent URL confusion attacks
    if (parsed.username || parsed.password) {
      return ''
    }
  } catch {
    return ''
  }
  return trimmed
}

/**
 * Validates that a URL uses http or https protocol (for media sources like images).
 */
export function isValidMediaUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim())
}
